import { useCallback, useEffect, useState } from "react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";

const number = (value, digits = 1) => value === null || value === undefined
  ? "—"
  : Number(value).toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const QUARTILES = [
  { key: "p0_25", label: "0–25%", color: "#DC2626" },
  { key: "p25_50", label: "25–50%", color: "#F59E0B" },
  { key: "p50_75", label: "50–75%", color: "#2563EB" },
  { key: "p75_100", label: "75–100%", color: "#16A34A" },
];

function ExamTooltip({ active, payload }) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="border border-border bg-background p-3 shadow-xl text-xs min-w-52">
      <div className="font-mono-data font-bold">{row.exam_name}</div>
      <div className="text-muted-foreground mt-1">{row.date ? new Date(`${row.date}T12:00:00`).toLocaleDateString("pt-BR") : "data não informada"}</div>
      {QUARTILES.map((quartile) => (
        <div className="mt-1" style={{ color: quartile.color }} key={quartile.key}>
          {quartile.label}: <b>{number(row[`${quartile.key}_score`])}%</b> · {row[`${quartile.key}_students`] || 0} aluno(s)
        </div>
      ))}
    </div>
  );
}

function GroupTable({ group, metric }) {
  const displayMetric = (value) => metric?.key === "time_minutes"
    ? `${number(Number(value || 0) / 60)} h`
    : `${number(value, 0)} questões`;
  return (
    <div className="border border-border bg-background min-w-0">
      <div className="px-4 py-3 border-b border-border" style={{ color: group?.color, backgroundColor: `${group?.color}12` }}>
        <div className="label-eyebrow">percentil {group?.label}</div>
        <div className="font-display text-2xl font-black mt-1">{group?.count || 0} aluno(s)</div>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4 border-b border-border text-center">
        <div><div className="label-eyebrow">média do quartil</div><div className="font-mono-data font-black mt-1">{displayMetric(group?.average_metric)}</div></div>
        <div><div className="label-eyebrow">média SAKI/Xm</div><div className="font-mono-data font-black text-primary mt-1">{number(group?.average_saki_score)}%</div></div>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-border">
        {(group?.students || []).map((student) => (
          <div key={student.student_id} className="px-4 py-3 grid grid-cols-[1fr_auto_auto] gap-3 items-center text-sm">
            <div className="min-w-0"><div className="font-semibold truncate">{student.student_name}</div><div className="text-xs text-muted-foreground">{student.saki_exam_count || 0} prova(s) SAKI/Xm</div></div>
            <div className="font-mono-data text-xs">{displayMetric(student.metric_value)}</div>
            <div className="font-mono-data font-bold text-primary">{number(student.saki_average_score)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuartileSection({ data, eyebrow }) {
  if (!data) return null;
  return (
    <section className="border border-border bg-card mt-5" data-testid={`medcof-quartile-${data.metric?.key}`}>
      <div className="p-5 border-b border-border flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="label-eyebrow">{eyebrow}</div>
          <h3 className="font-display text-3xl font-black mt-1">{data.metric?.label}</h3>
          <p className="text-sm text-muted-foreground mt-2">{data.comparable_students || 0} alunos com dados MEDCOF válidos no período, distribuídos entre 0–25%, 25–50%, 50–75% e 75–100%.</p>
          {(data.students_without_medcof > 0 || data.students_without_metric > 0) && (
            <p className="text-xs text-muted-foreground mt-1">
              {data.students_without_medcof || 0} sem registro MEDCOF e {data.students_without_metric || 0} sem esta métrica ficaram fora do cálculo.
            </p>
          )}
        </div>
      </div>

      <div className="p-5">
        <div className="flex flex-wrap gap-5 text-xs font-mono-data mb-3">
          {QUARTILES.map((quartile) => <span className="inline-flex items-center gap-2" key={quartile.key}><span className="h-3 w-3" style={{ backgroundColor: quartile.color }} />{quartile.label}</span>)}
        </div>
        <div className="h-[320px] border border-border p-3" data-testid="medcof-exam-chart">
          {(data.exam_chart || []).length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.exam_chart} margin={{ top: 12, right: 18, left: -8, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="exam_name" angle={-28} textAnchor="end" height={70} interval={0} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 10 }} />
                <Tooltip content={<ExamTooltip />} />
                {QUARTILES.map((quartile) => <Line key={quartile.key} type="monotone" connectNulls dataKey={`${quartile.key}_score`} name={quartile.label} stroke={quartile.color} strokeWidth={3} dot={{ r: 4 }} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-full grid place-items-center text-sm text-muted-foreground">Os quartis ainda não possuem provas SAKI/Xm comparáveis.</div>}
        </div>
        <div className="grid xl:grid-cols-2 gap-4 mt-4">
          {(data.groups || []).map((group) => <GroupTable key={group.key} group={group} metric={data.metric} />)}
        </div>
      </div>
    </section>
  );
}

export default function MedcofEngagementPanel() {
  const { user } = useAuth();
  const [options, setOptions] = useState({ schools: [], periods: [] });
  const [schoolId, setSchoolId] = useState("");
  const [period, setPeriod] = useState("");
  const [result, setResult] = useState({ questions: null, hours: null });
  const [loading, setLoading] = useState({ questions: false, hours: false });
  const [errors, setErrors] = useState({ questions: "", hours: "" });
  const isStudent = Boolean(user?.student_id || user?.account_type === "student");

  const loadOptions = useCallback(async (requestedSchool = "") => {
    const response = await api.get("/analytics/medcof/options", { params: requestedSchool ? { school_id: requestedSchool } : {} });
    const next = response.data || {};
    setOptions(next);
    setSchoolId(next.selected_school_id || requestedSchool || "");
    setPeriod((current) => current && (next.periods || []).some((item) => item.label === current) ? current : "all");
  }, []);

  useEffect(() => {
    if (!isStudent) loadOptions().catch((error) => toast.error("Falha ao abrir MEDCOF: " + fmtError(error)));
  }, [isStudent, loadOptions]);

  useEffect(() => {
    if (!schoolId || !period || isStudent) return;
    let active = true;
    setResult({ questions: null, hours: null });
    setErrors({ questions: "", hours: "" });
    setLoading({ questions: true, hours: true });

    const loadMetric = async (metric, resultKey) => {
      try {
        const response = await api.get("/analytics/medcof/comparison", {
          params: { school_id: schoolId, period_label: period, metric },
        });
        if (active) setResult((current) => ({
          ...current, [resultKey]: response.data?.quartiles?.[resultKey] || null,
        }));
      } catch (error) {
        const message = fmtError(error);
        if (active) setErrors((current) => ({ ...current, [resultKey]: message }));
      } finally {
        if (active) setLoading((current) => ({ ...current, [resultKey]: false }));
      }
    };
    Promise.allSettled([
      loadMetric("questions", "questions"),
      loadMetric("time_minutes", "hours"),
    ]);
    return () => { active = false; };
  }, [schoolId, period, isStudent]);

  const changeSchool = async (value) => {
    setSchoolId(value); setResult({ questions: null, hours: null });
    try { await loadOptions(value); } catch (error) { toast.error(fmtError(error)); }
  };
  if (isStudent) return null;
  return (
    <section className="border border-border bg-card p-6 mb-7" data-testid="medcof-engagement-panel">
      <div className="flex items-start gap-3">
        <ChartLineUp size={28} weight="duotone" className="text-primary shrink-0" />
        <div><div className="label-eyebrow">engajamento externo · MEDCOF</div><h2 className="font-display text-3xl font-black mt-1">Percentis de atividade e desempenho nas provas</h2><p className="text-sm text-muted-foreground mt-2 max-w-3xl">Dois gráficos grandes distribuem todos os alunos em quatro faixas, separadamente por questões resolvidas e tempo de uso, exibindo a média SAKI/Xm prova a prova.</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-6">
        <label><span className="label-eyebrow block mb-2">escola</span><select value={schoolId} onChange={(event) => changeSchool(event.target.value)} className="w-full border border-border bg-background px-3 py-2.5 text-sm">{options.schools?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label><span className="label-eyebrow block mb-2">período MEDCOF</span><select value={period} onChange={(event) => setPeriod(event.target.value)} className="w-full border border-border bg-background px-3 py-2.5 text-sm"><option value="all">Todo o histórico importado</option>{options.periods?.map((item) => <option key={`${item.start}-${item.end}`} value={item.label}>{item.label}</option>)}</select></label>
      </div>
      {loading.questions ? <div className="border border-border p-10 mt-5 text-center label-eyebrow">calculando percentis por questões…</div> : errors.questions ? <div className="border border-destructive/40 p-5 mt-5 text-sm text-destructive">Falha no gráfico de questões: {errors.questions}</div> : <QuartileSection data={result.questions} eyebrow="percentil por questões realizadas" />}
      {loading.hours ? <div className="border border-border p-10 mt-5 text-center label-eyebrow">calculando percentis por horas…</div> : errors.hours ? <div className="border border-destructive/40 p-5 mt-5 text-sm text-destructive">Falha no gráfico de horas: {errors.hours}</div> : <QuartileSection data={result.hours} eyebrow="percentil por horas na plataforma" />}
      {!loading.questions && !loading.hours && !errors.questions && !errors.hours && !result.questions && !result.hours && <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground mt-5">Ainda não há dados suficientes para formar quartis.</div>}
    </section>
  );
}
